import { Reducer } from "redux";
import {
    ADD_CGM_BRANCH,
    ADD_CGM_CRITICAL_BRANCH,
    ADD_CGM_OUTAGE,
    REMOVE_CGM_BRANCH,
    REMOVE_CRITICAL_BRANCH_CGM,
    REMOVE_OUTAGE_CGM,
    RESET_CGM_CRITICAL_BRANCH,
    SET_CGM_BRANCHES,
    SET_CGM_CRITICAL_BRANCH_BRANCH_INPUT,
    SET_CGM_OUTAGE_BRANCH_INPUT,
    SET_CGM_COUNTRY,
    SET_CGM_LORT,
    SET_CGM_VOLTAGE,
    TOGGLE_CGM_FORM_OPEN,
    TOGGLE_CGM_TIME_INTERVALS,
    SET_CGM_OUTAGE_BRANCH_EIC,
    SET_CGM_INPUT,
} from "../actions/cgmBranchesActions";

export interface ICGMBranch {
    id: string;
    timeInterval: number[];
    criticalBranch?: IBranch;
    imaxType: string;
    imaxFactor?: number;
    imaxA?: number;
    permanentImaxFactor?: number;
    temporaryImaxFactor?: number;
    permanentImaxA?: number;
    temporaryImaxA?: number;
    frmMw: string;
    minRamFactor: string;
    cnec: boolean;
    mnec: boolean;
    direction: string;
    tsoOrigin: string;
    outage?: IOutage;
}

export const cgmBranchInit: ICGMBranch = {
    id: "",
    timeInterval: [],
    imaxType: "dynamic",
    frmMw: "0",
    minRamFactor: "0",
    cnec: false,
    mnec: false,
    direction: "direct",
    tsoOrigin: "",
    outage: {
        id: "",
        name: "",
        location: "",
        branches: [],
    },
};

export interface IBranch {
    key?: string;
    node1: string;
    node2: string;
    orderCode: string;
    elementName: string;
    eic?: string;
    name?: string;
    timestamp?: string;
}

export interface IOutage {
    id: string;
    name: string;
    location: string;
    branches: IBranch[];
}

export interface CGMBranchesState {
    cgmBranches: ICGMBranch[];
    cgmBranch: ICGMBranch;
    formOpen: boolean;
    country: string;
    lort: string;
    voltage: string;
}

const initialState: CGMBranchesState = {
    cgmBranches: [],
    cgmBranch: cgmBranchInit,
    formOpen: false,
    country: "",
    lort: "",
    voltage: "",
};

export const cgmBranchesReducer: Reducer<CGMBranchesState> = (
    state: CGMBranchesState = initialState,
    action: any
): CGMBranchesState => {
    switch (action.type) {
        case SET_CGM_BRANCHES:
            return { ...state, cgmBranches: action.branches };
        case ADD_CGM_BRANCH:
            return {
                ...state,
                cgmBranches: state.cgmBranches.concat(action.branch),
            };
        case REMOVE_CGM_BRANCH:
            return {
                ...state,
                cgmBranches: state.cgmBranches.filter(x => x !== action.branch),
            };
        case TOGGLE_CGM_FORM_OPEN:
            return { ...state, formOpen: action.opt };
        case ADD_CGM_CRITICAL_BRANCH:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    criticalBranch: action.branch,
                },
            };
        case ADD_CGM_OUTAGE:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    outage: {
                        ...state.cgmBranch.outage!,
                        branches: state.cgmBranch.outage!.branches.concat(action.branch),
                    },
                },
            };
        case RESET_CGM_CRITICAL_BRANCH:
            return {
                ...state,
                cgmBranch: cgmBranchInit,
            };
        case REMOVE_CRITICAL_BRANCH_CGM:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    criticalBranch: undefined,
                },
            };
        case REMOVE_OUTAGE_CGM:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    outage: {
                        ...state.cgmBranch.outage!,
                        branches: state.cgmBranch.outage!.branches.filter(
                            x => x.key !== action.key
                        ),
                    },
                },
            };
        case TOGGLE_CGM_TIME_INTERVALS:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    timeInterval: state.cgmBranch.timeInterval.includes(action.timestamp)
                        ? state.cgmBranch.timeInterval
                              .filter(x => x !== action.timestamp)
                              .sort((a, b) => a - b)
                        : state.cgmBranch.timeInterval
                              .concat(action.timestamp)
                              .sort((a, b) => a - b),
                },
            };
        case SET_CGM_COUNTRY:
            return { ...state, country: action.country };
        case SET_CGM_LORT:
            return { ...state, lort: action.lort };
        case SET_CGM_VOLTAGE:
            return { ...state, voltage: action.voltage };
        case SET_CGM_CRITICAL_BRANCH_BRANCH_INPUT:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    criticalBranch: {
                        ...state.cgmBranch.criticalBranch!,
                        [action.e.target.name]: action.e.target.value,
                    },
                },
            };
        case SET_CGM_OUTAGE_BRANCH_INPUT:
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    outage: {
                        ...state.cgmBranch.outage!,
                        [action.e.target.name]: action.e.target.value,
                    },
                },
            };
        case SET_CGM_OUTAGE_BRANCH_EIC:
            const index = state.cgmBranch.outage!.branches.findIndex(x => x.key === action.key);
            const newBranches = [...state.cgmBranch.outage!.branches];
            newBranches[index] = { ...newBranches[index], eic: action.val };
            return {
                ...state,
                cgmBranch: {
                    ...state.cgmBranch,
                    outage: {
                        ...state.cgmBranch.outage!,
                        branches: newBranches,
                    },
                },
            };
        case SET_CGM_INPUT:
            if (action.data !== undefined) {
                const name = action.e.target.parentNode.parentNode.id;
                return {
                    ...state,
                    cgmBranch: {
                        ...state.cgmBranch,
                        [name]: action.data.value,
                    },
                };
            } else if (action.e.target.type === "checkbox") {
                const name = action.e.target.id;
                if (name === "cnec")
                    return {
                        ...state,
                        cgmBranch: {
                            ...state.cgmBranch,
                            [name]: !state.cgmBranch.cnec,
                        },
                    };
                else
                    return {
                        ...state,
                        cgmBranch: {
                            ...state.cgmBranch,
                            [name]: !state.cgmBranch.mnec,
                        },
                    };
            } else {
                const name = action.e.target.name;
                const value = action.e.target.value;
                return {
                    ...state,
                    cgmBranch: {
                        ...state.cgmBranch,
                        [name]: value,
                    },
                };
            }
        default:
            return state;
    }
};
